import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDatabase, ref, set, get } from 'firebase/database';
import { useAuth } from './AuthContext';

interface TermsAgreementContextType {
  hasAgreedToTerms: boolean;
  isLoading: boolean;
  error: string | null;
  agreeToTerms: () => Promise<void>;
  checkTermsAgreement: () => Promise<void>;
}

const TermsAgreementContext = createContext<TermsAgreementContextType | undefined>(undefined);

export const useTermsAgreement = () => {
  const context = useContext(TermsAgreementContext);
  if (!context) {
    throw new Error('useTermsAgreement must be used within a TermsAgreementProvider');
  }
  return context;
};

export const TermsAgreementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const db = getDatabase();

  const checkTermsAgreement = async () => {
    if (!currentUser) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const termsRef = ref(db, `users/${currentUser.uid}/termsAgreement`);
      const snapshot = await get(termsRef);
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      if (snapshot.exists()) {
        const data = snapshot.val();
        const agreedDate = data.timestamp?.slice(0, 10); // YYYY-MM-DD
        if (agreedDate === today) {
          setHasAgreedToTerms(true);
        } else {
          // Update agreement for today
          await set(termsRef, {
            agreed: true,
            timestamp: new Date().toISOString(),
            version: '1.0', // Update this when terms change
            platform: data.platform || 'web',
          });
          setHasAgreedToTerms(true);
        }
      } else {
        setHasAgreedToTerms(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check terms agreement');
      console.error('Error checking terms agreement:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const agreeToTerms = async () => {
    if (!currentUser) return;

    try {
      const termsRef = ref(db, `users/${currentUser.uid}/termsAgreement`);
      await set(termsRef, {
        agreed: true,
        timestamp: new Date().toISOString(),
        version: '1.0' // Update this when terms change
      });
      setHasAgreedToTerms(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save terms agreement');
      console.error('Error saving terms agreement:', err);
      throw err;
    }
  };

  useEffect(() => {
    checkTermsAgreement();
  }, [currentUser]);

  return (
    <TermsAgreementContext.Provider
      value={{
        hasAgreedToTerms,
        isLoading,
        error,
        agreeToTerms,
        checkTermsAgreement
      }}
    >
      {children}
    </TermsAgreementContext.Provider>
  );
}; 